import { useEffect, useState } from 'react';
import { makeStyles, Image, Card, Title1, Title3, Persona } from '@fluentui/react-components';
import ContactForm from '../../components/contactForm/ContactForm';
import IEntry from '../../models/blog/IEntry';
import blogService from '../../services/BlogService';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../app/SEO';
import ITeamMember from '../../models/ITeamMember';
import { Avatar } from '../../components/avatar/Avatar';
import Placeholder from './Placeholder';
import Status from '../../enums/Status';
import { dolphinTokens } from '../../utils/Theme';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        gap: '20px',
        backgroundColor: '#f5f5f5',
    },
    headerImageContainer: {
        position: "relative",
        width: "100%",
        maxWidth: "800px",
        height: "300px",
        margin: 0,
        borderRadius: '8px',
        overflow: 'hidden',
    },
    headerImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    section: {
        width: '100%',
        maxWidth: '800px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
    overview: {
        marginTop: 0
    },
    title: {
        marginBottom: 0
    },
    subtitle: {
        marginTop: 0,
        marginBottom: "1rem",
        color: dolphinTokens.colorLightBlue,
        fontWeight: "normal"
    },
    persona: {
        margin: "0 auto",
    },
    field: {
        margin: '1rem 0',
    },
    label: {
        fontWeight: 'bold',
    },
    content: {
        '& p': {
            marginBottom: '1rem',
            lineHeight: '1.6',
        },
        
        '& pre': {
            backgroundColor: '#f0f0f0',
            padding: '10px',
            borderRadius: '4px',
            overflowX: 'auto',
        },

        '& code': {
            backgroundColor: '#f0f0f0',
            padding: '2px 4px',
            borderRadius: '4px',
        },

        '& a': {
            color: '#0078d4',
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
            },
        },

        "& img": {
            width: "100%"
        }
    },
    contact: {
        width: '100%',
        backgroundColor: "white"
    }
});

const Blog = () => {
    const { slug } = useParams();
    const { t } = useTranslation("blog");

    const styles = useStyles();

    const [blog, setBlog] = useState<IEntry>(null!);
    const [status, setStatus] = useState<Status>(Status.LOADING);

    useEffect(() => {
        fetchBlog(slug!, setBlog, setStatus);
    }, []);

    if (status === Status.LOADING) {
        return <Placeholder />;
    }

    if (status === Status.LOAD_ERROR) {
        return null;
    }

    if (!blog) {
        return (
            <div>
                {t("errors.loadBlog")}
            </div>
        );
    }

    const jsonLD: {}[] = [
        {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blog.title.title,
            "author": {
                "@type": "Person",
                "name": blog.author
            },
            "datePublished": blog.published,
            "description": blog.metaDescription
        }
    ];

    const person: ITeamMember = {
        id: blog.authorId!,
        name: blog.author!,
        position: ""
    };

    const publishedDate = new Date(blog.published);
    const publishedStr = publishedDate.toDateString();

    const readTime = blog.readTime
        ? blog.readTime === 1
            ? t("readTime.one")
            : t("readTime.other", { count: blog.readTime })
        : "";

    return (
        <div>

            <SEO
                title={blog.title}
                metaDescription={blog.metaDescription}
                canonicalPath={`/blog/${blog.slug}`}
                jsonLD={jsonLD}
                openGraph={{
                    title: blog.title.title,
                    description: blog.metaDescription,
                    image: blog.image.url,
                    url: `blog/${blog.slug}`,
                    type: "article"
                }}
                />
                
            <div className={styles.container}>
                <div className={styles.headerImageContainer}>
                    <Image src={blog.image.url} alt={blog.image.altText} className={styles.headerImage} />
                </div>

                <Card className={`${styles.section} ${styles.overview}`}>
                    <Title1 block as="h1" align="center" className={styles.title}>
                        {blog.title.title}
                    </Title1>

                    {
                        blog.subtitle &&
                        <Title3 block as="h3" align="center" className={styles.subtitle}>
                            {blog.subtitle}
                        </Title3>
                    }

                    <Persona
                        name={person.name}
                        avatar={Avatar(person)}
                        secondaryText={t("published", { date: publishedStr })}
                        tertiaryText={readTime}
                        className={styles.persona}
                        size="extra-large"
                        />
                </Card>

                <Card className={styles.section}>
                    <div className={styles.field}>
                        <div className={styles.content} dangerouslySetInnerHTML={{ __html: blog.content }} />
                    </div>
                </Card>

                <div className={styles.contact}>
                    <ContactForm
                        title={t("contact.title")}
                        subtitle={t("contact.subtitle")}
                        />
                </div>
            </div>
        </div>
    );
};

export default Blog;

async function fetchBlog(
    slug: string,
    setBlog: (blog: IEntry) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const blog = await blogService.getBlog(slug);

        setBlog(blog);
        setStatus(Status.LOAD_SUCCESS);
    } catch (_) {
        setStatus(Status.LOAD_ERROR);
    }
}